/**
 * exportShippingTaxExcel.js
 * Exports shipping rates, weight thresholds, tax rates, tariff settings,
 * and a product simulation sheet into a multi-tab Excel workbook.
 *
 * Uses SheetJS (xlsx) — https://sheetjs.com
 */
import * as XLSX from 'xlsx';
import {
  ESTIMATE_BOOK_KG as DEFAULT_BOOK_KG,
  ESTIMATE_CD_KG as DEFAULT_CD_KG,
  ESTIMATE_BOOK_PRICE as DEFAULT_BOOK_PRICE,
  ESTIMATE_CD_PRICE as DEFAULT_CD_PRICE,
  estimateCartWeightKg
} from './shippingEstimateDefaults';

/* ── helpers ──────────────────────────────────────────────── */

function ws(data, opts) {
  const sheet = XLSX.utils.aoa_to_sheet(data, opts);
  return sheet;
}

function autoCols(sheet) {
  const ref = XLSX.utils.decode_range(sheet['!ref'] || 'A1');
  const widths = [];
  for (let C = ref.s.c; C <= ref.e.c; ++C) {
    let max = 8;
    for (let R = ref.s.r; R <= ref.e.r; ++R) {
      const cell = sheet[XLSX.utils.encode_cell({ r: R, c: C })];
      if (cell && cell.v != null) {
        max = Math.max(max, String(cell.v).length + 2);
      }
    }
    widths.push({ wch: Math.min(max, 40) });
  }
  sheet['!cols'] = widths;
  return sheet;
}

function pct(v) {
  return v != null ? `${(v * 100).toFixed(2)}%` : '';
}

function dollars(v) {
  return v != null ? `$${Number(v).toFixed(2)}` : '';
}

/* ── 1) Shipping Rates sheet ──────────────────────────────── */

function buildShippingRatesSheet(shippingSettings) {
  const header = [
    'Country Code', 'Country Name', 'Enabled',
    'Standard Rate ($)', 'Expedited Rate ($)', 'Express Rate ($)',
    'Free Threshold ($)',
    'Tariff Enabled', 'Tariff Rate', 'Tariff Exemption (USD)',
    'Tariff Notes'
  ];
  const rows = (shippingSettings || []).map(s => [
    s.country_code,
    s.country_name,
    s.enabled ? 'Yes' : 'No',
    Number(s.standard_rate) || 0,
    Number(s.expedited_rate) || 0,
    Number(s.express_rate) || 0,
    Number(s.free_threshold) || 0,
    s.tariff_enabled ? 'Yes' : 'No',
    pct(s.tariff_rate),
    s.tariff_exemption_threshold_usd != null ? dollars(s.tariff_exemption_threshold_usd) : '',
    s.tariff_notes || ''
  ]);
  return autoCols(ws([header, ...rows]));
}

/* ── 2) Weight Thresholds sheet ────────────────────────────── */

function buildWeightThresholdsSheet(shippingSettings) {
  const rows = [
    ['Weight-Based Shipping Thresholds'],
    [],
    ['Country', 'Type', 'Band #', 'Max Weight (kg)', 'Price (cents)', 'Price ($)']
  ];

  (shippingSettings || []).forEach(s => {
    let wt = s.weight_thresholds;
    if (typeof wt === 'string') { try { wt = JSON.parse(wt); } catch { wt = null; } }
    if (!wt) return;

    if (Array.isArray(wt)) {
      // Simple band array (US, INTL)
      if (!wt.length) return;
      const sorted = [...wt].sort((a, b) => a.weight_kg - b.weight_kg);
      sorted.forEach((b, i) => {
        rows.push([
          i === 0 ? s.country_code : '',
          i === 0 ? 'Weight Band' : '',
          i + 1, b.weight_kg, b.price_cents, dollars(b.price_cents / 100)
        ]);
      });
      rows.push([]);
    } else {
      // Extended format (CA): lettermail + parcel bands
      const lettermail = wt.lettermail_bands || wt.bands || [];
      const parcel = wt.parcel_bands || [];

      if (lettermail.length) {
        const sorted = [...lettermail].sort((a, b) => a.weight_kg - b.weight_kg);
        sorted.forEach((b, i) => {
          rows.push([
            i === 0 ? s.country_code : '',
            i === 0 ? 'Lettermail' : '',
            i + 1, b.weight_kg, b.price_cents, dollars(b.price_cents / 100)
          ]);
        });
      }

      if (parcel.length) {
        const sorted = [...parcel].sort((a, b) => a.weight_kg - b.weight_kg);
        sorted.forEach((b, i) => {
          rows.push([
            i === 0 && !lettermail.length ? s.country_code : '',
            i === 0 ? 'Parcel Band' : '',
            i + 1, b.weight_kg, b.price_cents, dollars(b.price_cents / 100)
          ]);
        });
      }

      if (lettermail.length || parcel.length) rows.push([]);
    }
  });

  if (rows.length === 3) {
    rows.push(['(No weight thresholds configured)']);
  }
  return autoCols(ws(rows));
}

/* ── 3) Canada Post Domestic sheet ─────────────────────────── */

function buildCanadaPostSheet(shippingSettings) {
  const ca = (shippingSettings || []).find(s => s.country_code === 'CA');
  const rows = [
    ['Canada Post Domestic Shipping Model'],
    ['Based on admin shipping_settings for CA'],
    []
  ];

  if (!ca) {
    rows.push(['No Canada (CA) shipping settings found.']);
    return autoCols(ws(rows));
  }

  let wt = ca.weight_thresholds;
  if (typeof wt === 'string') { try { wt = JSON.parse(wt); } catch { wt = null; } }

  const ext = (wt && !Array.isArray(wt)) ? wt : null;
  const bands = ext?.lettermail_bands || ext?.bands || (Array.isArray(wt) ? wt : []);

  // Lettermail bands
  rows.push(['── Lettermail (Oversized) Bands (≤0.5kg) ──']);
  rows.push(['Max Weight (kg)', 'Price (cents)', 'Price ($)']);
  if (bands.length) {
    [...bands].sort((a, b) => a.weight_kg - b.weight_kg).forEach(b => {
      rows.push([b.weight_kg, b.price_cents, dollars(b.price_cents / 100)]);
    });
  } else {
    rows.push(['(none)']);
  }
  rows.push([]);

  // Parcel bands (0.5kg+)
  const parcelBands = ext?.parcel_bands || [];
  rows.push(['── Parcel Bands (0.5kg–10kg+) ──']);
  rows.push(['Max Weight (kg)', 'Price (cents)', 'Price ($)']);
  if (parcelBands.length) {
    [...parcelBands].sort((a, b) => a.weight_kg - b.weight_kg).forEach(b => {
      rows.push([b.weight_kg, b.price_cents, dollars(b.price_cents / 100)]);
    });
  } else {
    rows.push(['(none — multi-book orders fall back to flat-rate boxes or standard rate)']);
  }
  rows.push([]);

  // Flat-rate boxes
  rows.push(['── Flat-Rate Prepaid Boxes ──']);
  const flatBoxes = ext?.flat_rate_boxes || [];
  const flatCents = ext?.flat_rate_boxes_cents || [];
  if (flatBoxes.length) {
    rows.push(['Price (cents)', 'Max Interior Length (cm)']);
    flatBoxes.forEach(b => {
      rows.push([b.price_cents, b.max_length_cm || 'No limit']);
    });
  } else if (flatCents.length) {
    rows.push(['Price (cents)']);
    flatCents.forEach(c => rows.push([c]));
  } else {
    rows.push(['(none)']);
  }
  rows.push([]);

  // Canada Post config values
  rows.push(['── Configuration Values ──']);
  rows.push(['Parameter', 'Value']);
  rows.push(['Lettermail Max Kg', ext?.lettermail_max_kg ?? 0.5]);
  rows.push(['Flat-Rate Max Kg', ext?.flat_rate_max_kg ?? 5]);
  rows.push(['Regular Parcel Extra per Kg (cents)', ext?.regular_parcel_extra_per_kg_cents ?? 100]);
  rows.push(['Xpresspost ≤1kg (cents)', ext?.xpresspost_1kg_cents ?? '(not set)']);
  rows.push(['Shipment Longest Side (cm)', ext?.ca_shipment_longest_cm ?? '(not set)']);
  rows.push([]);

  // Algorithm description
  rows.push(['── Standard Domestic Algorithm ──']);
  rows.push(['Step', 'Description']);
  rows.push(['1', 'If subtotal ≥ free-threshold → FREE shipping']);
  rows.push(['2', 'Option A: If weight ≤ lettermail_max_kg → use lettermail band price']);
  rows.push(['3', 'Option B: If weight ≤ flat_rate_max_kg → cheapest flat-rate box where longest side fits']);
  rows.push(['4', 'Option C: If weight > lettermail_max_kg → use parcel band price (0.6kg–10kg tiers)']);
  rows.push(['5', 'Option D: If weight > flat_rate_max_kg → standard rate + (excess_kg × extra_per_kg_cents)']);
  rows.push(['6', 'Pick CHEAPEST of eligible options A–D']);
  rows.push(['7', 'Compare product minimum_shipping_cents; use MAX(shipping, product minimum)']);
  rows.push(['Result', 'Cheapest of eligible options, floored by product minimum']);

  return autoCols(ws(rows));
}

/* ── 4) Tax Rates sheet ─────────────────────────────────────── */

function buildTaxRatesSheet(taxSettings) {
  const rows = [
    ['Tax Rates by Zone & Province/State'],
    [],
    ['Zone', 'Province/State Code', 'Province/State Name',
     'General Rate', 'Books Rate (GST)', 'Shipping Tax Rate', 'Notes', 'Enabled']
  ];

  (taxSettings || []).forEach(t => {
    rows.push([
      t.zone,
      t.province_code || '(all)',
      t.province_name || '',
      pct(t.general_rate),
      pct(t.books_rate),
      pct(t.shipping_tax_rate),
      t.notes || '',
      t.enabled ? 'Yes' : 'No'
    ]);
  });

  rows.push([]);
  rows.push(['── Tax Calculation Algorithm ──']);
  rows.push(['Step', 'Description']);
  rows.push(['1', 'Classify each item: exempt / book (GST only) / regular (general rate) / custom rate']);
  rows.push(['2', 'Books → taxed at books_rate (usually 5% GST, point-of-sale rebate)']);
  rows.push(['3', 'Regular items → taxed at general_rate (HST/PST+GST varies by province)']);
  rows.push(['4', 'Custom rate items → taxed at product-level tax_rate']);
  rows.push(['5', 'Shipping → taxed at shipping_tax_rate']);
  rows.push(['6', 'Total tax = sum of all above']);
  rows.push([]);
  rows.push(['── Point-of-Sale Rebate Categories ──']);
  rows.push(['Category']);
  ['book', 'books', 'collection', 'collections', 'publication', 'printed book', 'audio recording']
    .forEach(c => rows.push([c]));

  return autoCols(ws(rows));
}

/* ── 5) Tariff Calculation sheet ───────────────────────────── */

function buildTariffSheet(shippingSettings) {
  const us = (shippingSettings || []).find(s => s.country_code === 'US');
  const rows = [
    ['US Tariff Calculation'],
    [],
    ['Parameter', 'Value'],
    ['Tariff Enabled', us?.tariff_enabled ? 'Yes' : 'No'],
    ['Tariff Rate', pct(us?.tariff_rate || 0.35)],
    ['Exemption Threshold (USD)', dollars(us?.tariff_exemption_threshold_usd || 100)],
    ['CAD → USD Exchange Rate (approx)', 0.74],
    ['Notes', us?.tariff_notes || ''],
    [],
    ['── Tariff Calculation Algorithm ──'],
    ['Step', 'Description'],
    ['1', 'Only applies to USA orders'],
    ['2', 'Convert cart subtotal CAD → USD (× 0.74 approx)'],
    ['3', 'tariffable_amount = MAX(0, subtotal_USD − exemption_threshold)'],
    ['4', 'tariff_USD = tariffable_amount × tariff_rate'],
    ['5', 'tariff_CAD = tariff_USD ÷ 0.74'],
    ['6', 'Final tariff (cents) = ROUND(tariff_CAD × 100)'],
    [],
    ['── Example ──'],
    ['Cart Subtotal (CAD)', '$200.00'],
    ['Subtotal (USD)', '$148.00'],
    ['Exemption', '$100.00'],
    ['Tariffable Amount', '$48.00'],
    ['Tariff (USD, 35%)', '$16.80'],
    ['Tariff (CAD)', '$22.70'],
    [],
    ['── REVIEW: Potential Exemptions ──'],
    ['Item', 'Details'],
    ['Section 321 de minimis', 'US allows $800 USD duty-free for commercial shipments. Personal gifts under $100 USD are also exempt.'],
    ['Media exemption', 'Printed books, CDs, and DVDs are often classified as duty-free media regardless of value (HTS Chapter 49 / 8524).'],
    ['CUSMA/USMCA', 'Goods originating in Canada may qualify for preferential treatment under CUSMA, but postal shipments currently require duty prepayment.'],
    ['Current postal suspension', 'The US suspended the $800 de minimis for Canada Post parcels (as of 2025). Commercial couriers (UPS/FedEx) may still benefit.'],
    [],
    ['ACTION', 'Verify whether your specific product mix (books, CDs, DVDs) qualifies for media exemption.'],
    ['ACTION', 'If books are duty-free, consider disabling tariff or setting rate to 0% for book-only orders.'],
    ['ACTION', 'The 0.74 CAD→USD rate is hardcoded. Update periodically or integrate a live rate API.'],
  ];

  return autoCols(ws(rows));
}

/* ── 6) Product Simulator sheet (with real Excel formulas) ─── */

function buildSimulatorSheet(shippingSettings, taxSettings) {
  const ca  = (shippingSettings || []).find(s => s.country_code === 'CA');
  const us  = (shippingSettings || []).find(s => s.country_code === 'US');
  const intl = (shippingSettings || []).find(s => s.country_code === 'INTL');

  const {
    caShipping, usShipping, intlShipping
  } = buildWeightShippingFns(shippingSettings);
  const flatCfg = loadFlatRateConfig(shippingSettings);

  const caStd  = Number(ca?.standard_rate) || 8.99;
  const usStd  = Number(us?.standard_rate) || 5.99;
  const intlStd = Number(intl?.standard_rate) || 24.99;
  const caFree  = Number(ca?.free_threshold) || 50;
  const usFree  = Number(us?.free_threshold) || 75;
  const intlFree = Number(intl?.free_threshold) || 200;

  const tariffRate = Number(us?.tariff_rate) || 0.35;
  const tariffExemption = Number(us?.tariff_exemption_threshold_usd) || 100;
  const tariffEnabled = us?.tariff_enabled ? true : false;
  const cadToUsd = 0.74;

  const caProvinces = (taxSettings || []).filter(t => t.zone === 'CA');

  // Product columns: B=Prod1, C=Prod2, D=Prod3, E=Prod4, F=Prod5
  const PCOLS = 5;
  const prodHeaders = Array.from({ length: PCOLS }, (_, i) => `Product ${i + 1}`);

  const sampleProducts = [
    { name: 'Cape Breton Collection', price: DEFAULT_BOOK_PRICE, qty: 1, wt: 0.35, book: 'Yes', minShip: 0, minShipIntl: 0, longest: 28 },
    { name: 'Jerry Holland CD',       price: DEFAULT_CD_PRICE, qty: 2, wt: 0.12, book: 'No',  minShip: 0, minShipIntl: 0, longest: 14 },
    { name: 'Buddy DVD',              price: 24.95, qty: 1, wt: 0.15, book: 'No',  minShip: 0, minShipIntl: 0, longest: 19 },
    { name: '',                        price: 0,     qty: 0, wt: 0,    book: 'No',  minShip: 0, minShipIntl: 0, longest: 0 },
    { name: '',                        price: 0,     qty: 0, wt: 0,    book: 'No',  minShip: 0, minShipIntl: 0, longest: 0 },
  ];

  // Rows are 1-indexed in Excel. We build zero-indexed arrays, but formulas use 1-indexed.
  // Row layout (0-indexed):
  //   0  Title
  //   1  Instructions
  //   2  blank
  //   3  ── PRODUCT INPUT ──
  //   4  Headers:    (A)Label  (B)Prod1  (C)Prod2 ...
  //   5  Name        row 6 in Excel
  //   6  Price       row 7
  //   7  Qty         row 8
  //   8  Weight/unit row 9
  //   9  Is Book?    row 10
  //  10  Min Ship    row 11
  //  11  Min Ship Intl row 12
  //  12  Longest cm  row 13
  //  13  blank
  //  14  ── CALCULATED ──
  //  15  Subtotal     row 16  (formulas)
  //  16  Books Subtot row 17
  //  17  Non-Books    row 18
  //  18  Weight       row 19
  //  19  Max Min Ship row 20
  //  20  Max Min Intl row 21
  //  21  blank
  //  22  ── SHIPPING BY DEST ──
  //  23  Headers
  //  24  Canada       row 25
  //  25  USA          row 26
  //  26  Intl         row 27
  //  27  blank
  //  28  ── CA PROVINCES ──
  //  29  Headers
  //  30+ province rows  (row 31+ in Excel)
  //  then blank, US+INTL summary, tariff, order totals

  const eR = (r) => r + 1; // zero-index → Excel row number
  // Helper: SUMPRODUCT for "book" items price*qty
  // Price row=6(idx), Qty row=7(idx), IsBook row=9(idx)
  const bookSubF = `SUMPRODUCT((B7:F7)*(B8:F8)*(UPPER(B10:F10)="YES"))`;
  const nonBookSubF = `SUMPRODUCT((B7:F7)*(B8:F8)*(UPPER(B10:F10)<>"YES"))`;
  const totalSubF = `SUMPRODUCT(B7:F7*B8:F8)`;
  const totalWtF = `SUMPRODUCT(B9:F9*B8:F8)`;
  const maxMinShipF = `MAX(B11:F11)`;
  const maxMinIntlF = `MAX(B12:F12)`;

  // ── Build rows ──
  const data = [];

  // R0: Title
  data.push(['SHIPPING, TAX & TARIFF SIMULATOR', '', '', '', '', '']);
  // R1: Instructions
  data.push(['Edit the blue product cells (rows 6-13) then see live calculations below.', '', '', '', '', '']);
  // R2: blank
  data.push([]);
  // R3: section
  data.push(['── PRODUCT INPUT (edit these) ──']);
  // R4: headers
  data.push(['', ...prodHeaders]);
  // R5: Name
  data.push(['Product Name', ...sampleProducts.map(p => p.name)]);
  // R6: Price  (Excel row 7)
  data.push(['Unit Price (CAD $)', ...sampleProducts.map(p => p.price)]);
  // R7: Qty  (Excel row 8)
  data.push(['Quantity', ...sampleProducts.map(p => p.qty)]);
  // R8: Weight per unit (Excel row 9)
  data.push(['Weight per unit (kg)', ...sampleProducts.map(p => p.wt)]);
  // R9: IsBook  (Excel row 10)
  data.push(['Is Book? (Yes/No)', ...sampleProducts.map(p => p.book)]);
  // R10: Min shipping  (Excel row 11)
  data.push(['Min Shipping (cents)', ...sampleProducts.map(p => p.minShip)]);
  // R11: Min ship intl  (Excel row 12)
  data.push(['Min Shipping Intl (cents)', ...sampleProducts.map(p => p.minShipIntl)]);
  // R12: Longest  (Excel row 13)
  data.push(['Longest Side (cm)', ...sampleProducts.map(p => p.longest)]);
  // R13: blank
  data.push([]);

  // R14: section
  data.push(['── CALCULATED TOTALS ──']);
  // R15: Subtotal  (Excel row 16)
  data.push(['Subtotal (CAD $)', { f: totalSubF }]);
  // R16: Books subtotal  (Excel row 17)
  data.push(['Books Subtotal ($)', { f: bookSubF }]);
  // R17: Non-books  (Excel row 18)
  data.push(['Non-Books Subtotal ($)', { f: nonBookSubF }]);
  // R18: Total billable weight (Excel row 19)
  data.push(['Estimated Billable Weight (kg)', { f: totalWtF }]);
  // R19: Max min ship  (Excel row 20)
  data.push(['Max Product Min Shipping (cents)', { f: maxMinShipF }]);
  // R20: Max min intl  (Excel row 21)
  data.push(['Max Product Min Intl Ship (cents)', { f: maxMinIntlF }]);
  // R21: blank
  data.push([]);

  // R22: section
  data.push(['── SHIPPING COST BY DESTINATION (standard/min rate) ──']);
  // R23: headers  (Excel row 24)
  data.push(['Destination', 'Est. Weight (kg)', 'Std Rate ($)', 'Free Threshold ($)', 'Shipping ($)', 'Notes']);

  // R24: Canada  (Excel row 25)
  data.push([
    'Canada (CA)', { f: 'B19' }, caStd, caFree,
    { f: `IF(B16>=D25,0,MAX(C25,B20/100))` },
    'Standard/min rate; see weight-band comparison below'
  ]);

  // R25: USA  (Excel row 26)
  data.push([
    'United States (US)', { f: 'B19' }, usStd, usFree,
    { f: `IF(B16>=D26,0,MAX(C26,B20/100))` },
    'Standard/min rate; see weight-band comparison below'
  ]);

  // R26: International  (Excel row 27)
  data.push([
    'International (INTL)', { f: 'B19' }, intlStd, intlFree,
    { f: `IF(B16>=D27,0,MAX(C27,B21/100))` },
    'Standard/min rate; see weight-band comparison below'
  ]);

  // Weight-band + flat-rate comparison (pre-calculated at sample weight for reference)
  const sampleWt = sampleProducts.reduce((t, p) => t + p.wt * p.qty, 0);
  const sampleSub = sampleProducts.reduce((t, p) => t + p.price * p.qty, 0);
  const sampleBooks = sampleProducts.filter(p => p.book === 'Yes').reduce((t, p) => t + p.qty, 0);
  const sampleCds = sampleProducts.filter(p => p.book !== 'Yes' && p.qty > 0).reduce((t, p) => t + p.qty, 0);

  data.push([]);
  data.push(['── WEIGHT-BASED & FLAT-RATE COMPARISON (updates when you change weight in B19) ──']);
  data.push([
    'Destination', 'Weight (kg)', 'Weight-Band Ship ($)', 'Flat-Rate Ship ($)', 'Difference', 'Covers Postage?'
  ]);

  const wbCa = caShipping(sampleWt, sampleSub) / 100;
  const wbUs = usShipping(sampleWt) / 100;
  const wbIntl = intlShipping(sampleWt) / 100;

  let flatCa = null, flatUs = null, flatIntl = null;
  if (flatCfg?.enabled) {
    const fc = (dest, bq, cq) => calcFlatRateOrder(flatCfg, dest, bq, cq);
    flatCa = fc('CA', sampleBooks, sampleCds);
    flatUs = fc('US', sampleBooks, sampleCds);
    flatIntl = fc('INTL', sampleBooks, sampleCds);
  }

  const compRows = [
    ['Canada (CA)'],
    ['United States (US)'],
    ['International (INTL)']
  ];

  // Pre-fill sample values; edit product qty/weight above to change B19 and recalculate manually
  const wbVals = [wbCa, wbUs, wbIntl];
  const flatVals = [flatCa, flatUs, flatIntl];

  compRows.forEach((row, i) => {
    const wb = wbVals[i];
    const flat = flatVals[i];
    row.push(
      { f: 'B19' },
      wb,
      flat ? flat.shippingCad : 'N/A (flat off)',
      flat ? +(flat.shippingCad - wb).toFixed(2) : 'N/A',
      flat ? coverageLabel(Math.round(flat.shippingCad * 100), Math.round(wb * 100)) : 'N/A'
    );
    data.push(row);
  });

  data.push([]);
  data.push([`Sample cart weight: ${sampleWt.toFixed(2)} kg (${sampleBooks} book(s), ${sampleCds} CD(s)) — edit product qty/weight above to recalculate B19`]);
  data.push(['Weight-band shipping uses admin weight thresholds. Flat-rate uses base shipping & handling + a per-item postage adjustment.']);
  data.push(['SHORT = flat rate may not cover postage — raise rates or refund difference at checkout.']);

  // R27: blank
  data.push([]);
  // R28: section
  data.push(['── TAX BY CANADIAN PROVINCE ──']);
  // R29: headers  (Excel row 30)
  // A=Province, B=Code, C=General%, D=Books%, E=ShippingTax%, F=TaxOnBooks, G=TaxOnNonBooks, H=TaxOnShipping, I=TotalTax, J=OrderTotal
  data.push([
    'Province', 'Code', 'General Rate', 'Books Rate',
    'Ship Tax Rate', 'Tax on Books ($)', 'Tax on Non-Books ($)',
    'Tax on Shipping ($)', 'Total Tax ($)', 'Order Total ($)'
  ]);

  // Province rows — E25 = CA standard/min shipping; use weight-band row if you switch to band-based ship
  const provStartExcel = data.length + 1;
  caProvinces.forEach((p, i) => {
    const er = provStartExcel + i;
    data.push([
      p.province_name || '',
      p.province_code || '',
      p.general_rate || 0,
      p.books_rate || 0,
      p.shipping_tax_rate || 0,
      { f: `ROUND(B17*D${er},2)` },
      { f: `ROUND(B18*C${er},2)` },
      { f: `ROUND(E25*E${er},2)` },
      { f: `F${er}+G${er}+H${er}` },
      { f: `B16+E25+I${er}` },
    ]);
  });

  // blank
  data.push([]);
  // ── USA SECTION ──
  data.push(['── USA ORDER (no tax, with tariff) ──']);
  // Headers
  data.push(['', 'Value']);

  const usSubtotalRow = data.length;
  data.push(['Subtotal (CAD $)', { f: 'B16' }]);

  const usShipRow = data.length;
  data.push(['Shipping (CAD $)', { f: 'E26' }]);

  data.push(['Tax', 0]);

  const usCadToUsdRow = data.length;
  data.push(['CAD→USD Rate', cadToUsd]);

  const usSubUsdRow = data.length;
  data.push(['Subtotal (USD)', { f: `B${eR(usSubtotalRow)}*B${eR(usCadToUsdRow)}` }]);

  const usTariffRateRow = data.length;
  data.push(['Tariff Rate', tariffRate]);

  const usTariffExRow = data.length;
  data.push(['Tariff Exemption (USD)', tariffExemption]);

  const usTariffableRow = data.length;
  data.push(['Tariffable Amount (USD)', { f: `MAX(0,B${eR(usSubUsdRow)}-B${eR(usTariffExRow)})` }]);

  const usTariffUsdRow = data.length;
  data.push([
    'Tariff (USD)',
    { f: tariffEnabled
      ? `B${eR(usTariffableRow)}*B${eR(usTariffRateRow)}`
      : '0' }
  ]);

  const usTariffCadRow = data.length;
  data.push([
    'Tariff (CAD $)',
    { f: tariffEnabled
      ? `ROUND(B${eR(usTariffUsdRow)}/B${eR(usCadToUsdRow)},2)`
      : '0' }
  ]);

  const usOrderTotalRow = data.length;
  data.push([
    'USA Order Total (CAD $)',
    { f: `B${eR(usSubtotalRow)}+B${eR(usShipRow)}+B${eR(usTariffCadRow)}` }
  ]);

  data.push([tariffEnabled ? '' : '(Tariff is currently DISABLED in admin settings)']);

  // blank
  data.push([]);

  // ── INTERNATIONAL SECTION ──
  data.push(['── INTERNATIONAL ORDER (no tax, no tariff) ──']);
  data.push(['', 'Value']);

  const intlSubRow = data.length;
  data.push(['Subtotal (CAD $)', { f: 'B16' }]);

  const intlShipRow = data.length;
  data.push(['Shipping (CAD $)', { f: 'E27' }]);

  data.push(['Tax', 0]);
  data.push(['Tariff', 'N/A']);

  const intlTotalRow = data.length;
  data.push([
    'Intl Order Total (CAD $)',
    { f: `B${eR(intlSubRow)}+B${eR(intlShipRow)}` }
  ]);

  // blank
  data.push([]);

  // ── SUMMARY COMPARISON TABLE ──
  data.push(['── ORDER TOTAL COMPARISON ──']);
  data.push(['Destination', 'Subtotal', 'Shipping', 'Tax', 'Tariff', 'Order Total']);

  // CA provinces summary (pick a few key ones)
  const keyProvs = ['NS', 'ON', 'BC', 'AB', 'QC'];
  keyProvs.forEach(code => {
    const pIdx = caProvinces.findIndex(p => p.province_code === code);
    if (pIdx < 0) return;
    const provExcelRow = provStartExcel + pIdx;
    data.push([
      `Canada - ${caProvinces[pIdx].province_name}`,
      { f: 'B16' },
      { f: 'E25' },
      { f: `I${provExcelRow}` },
      'N/A',
      { f: `J${provExcelRow}` },
    ]);
  });

  // USA row
  data.push([
    'United States',
    { f: 'B16' },
    { f: 'E26' },
    0,
    { f: `B${eR(usTariffCadRow)}` },
    { f: `B${eR(usOrderTotalRow)}` },
  ]);

  // INTL row
  data.push([
    'International',
    { f: 'B16' },
    { f: 'E27' },
    0,
    'N/A',
    { f: `B${eR(intlTotalRow)}` },
  ]);

  // Build sheet
  const sheet = XLSX.utils.aoa_to_sheet(data);

  // Style: set column widths
  sheet['!cols'] = [
    { wch: 30 }, // A
    { wch: 16 }, // B
    { wch: 16 }, // C
    { wch: 16 }, // D
    { wch: 16 }, // E
    { wch: 16 }, // F
    { wch: 18 }, // G
    { wch: 18 }, // H
    { wch: 14 }, // I
    { wch: 16 }, // J
  ];

  // Format rate columns as percentages in province rows
  caProvinces.forEach((_, i) => {
    const er = provStartExcel + i;
    ['C', 'D', 'E'].forEach(col => {
      const addr = `${col}${er}`;
      if (sheet[addr]) sheet[addr].z = '0.00%';
    });
  });

  // Format tariff rate as percentage
  const trAddr = `B${eR(usTariffRateRow)}`;
  if (sheet[trAddr]) sheet[trAddr].z = '0.00%';

  // Format dollar cells
  const dollarFmt = '$#,##0.00';
  const dollarCells = [
    `B${eR(15)}`, `B${eR(16)}`, `B${eR(17)}`, // subtotals
  ];
  // Shipping row dollars
  ['E25', 'E26', 'E27'].forEach(a => {
    if (sheet[a]) sheet[a].z = dollarFmt;
  });
  dollarCells.forEach(a => {
    if (sheet[a]) sheet[a].z = dollarFmt;
  });
  // Province tax & total columns
  caProvinces.forEach((_, i) => {
    const er = provStartExcel + i;
    ['F', 'G', 'H', 'I', 'J'].forEach(col => {
      const addr = `${col}${er}`;
      if (sheet[addr]) sheet[addr].z = dollarFmt;
    });
  });
  // USA section dollars
  [usSubtotalRow, usShipRow, usSubUsdRow, usTariffableRow,
   usTariffUsdRow, usTariffCadRow, usOrderTotalRow].forEach(r => {
    const addr = `B${eR(r)}`;
    if (sheet[addr]) sheet[addr].z = dollarFmt;
  });
  // INTL section dollars
  [intlSubRow, intlShipRow, intlTotalRow].forEach(r => {
    const addr = `B${eR(r)}`;
    if (sheet[addr]) sheet[addr].z = dollarFmt;
  });

  return sheet;
}

/* ── Weight helpers (shared across simulator sheets) ─────────── */

function buildWeightShippingFns(shippingSettings) {
  const ca = (shippingSettings || []).find(s => s.country_code === 'CA');
  const us = (shippingSettings || []).find(s => s.country_code === 'US');
  const intl = (shippingSettings || []).find(s => s.country_code === 'INTL');

  let caWt = ca?.weight_thresholds;
  if (typeof caWt === 'string') { try { caWt = JSON.parse(caWt); } catch { caWt = null; } }
  const caLettermail = caWt?.lettermail_bands || [];
  const caParcel = caWt?.parcel_bands || [];
  const caFlatBoxes = caWt?.flat_rate_boxes || [];
  const caMaxLetter = caWt?.lettermail_max_kg ?? 0.5;
  const caFuelPct = Number(caWt?.fuel_surcharge_pct) || 0;

  let usBands = us?.weight_thresholds;
  if (typeof usBands === 'string') { try { usBands = JSON.parse(usBands); } catch { usBands = []; } }
  if (!Array.isArray(usBands)) usBands = [];

  let intlBands = intl?.weight_thresholds;
  if (typeof intlBands === 'string') { try { intlBands = JSON.parse(intlBands); } catch { intlBands = []; } }
  if (!Array.isArray(intlBands)) intlBands = [];

  const caFreeThreshold = Number(ca?.free_threshold) || 500;

  function applyCaFuel(baseCents) {
    if (!caFuelPct) return baseCents;
    return baseCents + Math.round(baseCents * caFuelPct / 100);
  }

  function caShipping(weightKg, subtotalCad = 0) {
    if (subtotalCad >= caFreeThreshold) return 0;
    if (weightKg <= 0) return Math.round((Number(ca?.standard_rate) || 16) * 100);

    const candidates = [];
    if (weightKg <= caMaxLetter && caLettermail.length) {
      const lm = lookupBandPrice(weightKg, caLettermail);
      if (lm != null) candidates.push(lm);
    }
    if (caParcel.length) {
      const p = lookupBandPrice(weightKg, caParcel);
      if (p != null) candidates.push(applyCaFuel(p));
    }
    if (weightKg <= (caWt?.flat_rate_max_kg ?? 5) && caFlatBoxes.length) {
      candidates.push(Math.min(...caFlatBoxes.map(b => b.price_cents)));
    }
    if (candidates.length) return Math.min(...candidates);
    return Math.round((Number(ca?.standard_rate) || 16) * 100);
  }

  function usShipping(weightKg) {
    const p = lookupBandPrice(weightKg, usBands);
    return p ?? Math.round((Number(us?.standard_rate) || 15.99) * 100);
  }

  function intlShipping(weightKg) {
    const p = lookupBandPrice(weightKg, intlBands);
    return p ?? Math.round((Number(intl?.standard_rate) || 24.99) * 100);
  }

  return {
    ca, us, intl, caLettermail, caParcel, caFlatBoxes, caMaxLetter, caFuelPct,
    usBands, intlBands, caShipping, usShipping, intlShipping, caFreeThreshold
  };
}

function loadFlatRateConfig(shippingSettings) {
  for (const s of shippingSettings || []) {
    let frc = s.flat_rate_config;
    if (typeof frc === 'string') { try { frc = JSON.parse(frc); } catch { frc = null; } }
    if (frc) return frc;
  }
  return null;
}

function tieredPerItemCents(qty, rates) {
  const tiers = rates?.tiers;
  if (!tiers || !Array.isArray(tiers) || tiers.length === 0) {
    return (rates?.per_item_cents || 0) * qty;
  }
  const sorted = [...tiers].sort((a, b) => (a.up_to ?? Infinity) - (b.up_to ?? Infinity));
  let rem = qty, prev = 0, total = 0;
  for (const t of sorted) {
    if (rem <= 0) break;
    const slot = (t.up_to ?? Infinity) - prev;
    const n = Math.min(rem, slot);
    total += n * (t.per_item_cents || 0);
    rem -= n;
    prev = t.up_to ?? Infinity;
  }
  if (rem > 0) total += rem * (sorted[sorted.length - 1].per_item_cents || 0);
  return total;
}

function flatRateBooksOnlyCents(cfg, dest, bookQty) {
  if (!cfg?.destinations?.[dest]) return null;
  const bookRates = cfg.destinations[dest].Book || { handling_cents: 0, per_item_cents: 0 };
  let base = (bookRates.handling_cents || 0) + tieredPerItemCents(bookQty, bookRates);
  const fuelPct = Number(cfg.fuel_surcharge_pct) || 0;
  if (fuelPct > 0) base += Math.round(base * fuelPct / 100);
  return base;
}

function coverageLabel(flatCents, weightCents) {
  if (weightCents == null || flatCents == null) return 'N/A';
  const diff = flatCents - weightCents;
  if (diff >= 0) return `OK (+$${(diff / 100).toFixed(2)})`;
  return `SHORT $${(Math.abs(diff) / 100).toFixed(2)}`;
}

/** Excel INDEX/MATCH band lookup: cols A=kg, B=price($) */
function bandLookupFormula(weightCell, dataStart, dataEnd) {
  if (dataEnd < dataStart) return '0';
  return `IF(${weightCell}=0,0,INDEX($B$${dataStart}:$B$${dataEnd},MATCH(${weightCell},$A$${dataStart}:$A$${dataEnd},1)))`;
}

/** Flat-rate $ = MAX(bookH,cdH) + books*bookPI + cds*cdPI, + fuel% */
function flatRateFormula(rateRow, booksCell, cdsCell, fuelCell) {
  return `ROUND((MAX(C${rateRow},E${rateRow})+${booksCell}*D${rateRow}+${cdsCell}*F${rateRow})*(1+${fuelCell}/100),2)`;
}

function shortfallFormula(flatCell, weightCell) {
  return `IF(${flatCell}-${weightCell}>=0,"OK","SHORT $"&TEXT(ABS(${flatCell}-${weightCell}),"0.00"))`;
}

function defaultFlatDestinations() {
  return {
    CA:   {
      Book: { handling_cents: 1100, per_item_cents: 200, tiers: [{ up_to: 2, per_item_cents: 200 }, { up_to: 5, per_item_cents: 350 }, { up_to: null, per_item_cents: 300 }] },
      CD:   { handling_cents: 500,  per_item_cents: 150, tiers: [{ up_to: 2, per_item_cents: 150 }, { up_to: null, per_item_cents: 200 }] }
    },
    US:   {
      Book: { handling_cents: 1500, per_item_cents: 300, tiers: [{ up_to: 2, per_item_cents: 300 }, { up_to: 5, per_item_cents: 600 }, { up_to: null, per_item_cents: 500 }] },
      CD:   { handling_cents: 500,  per_item_cents: 150, tiers: [{ up_to: 2, per_item_cents: 150 }, { up_to: null, per_item_cents: 250 }] }
    },
    INTL: {
      Book: { handling_cents: 2000, per_item_cents: 500, tiers: [{ up_to: 2, per_item_cents: 500 }, { up_to: 5, per_item_cents: 1200 }, { up_to: null, per_item_cents: 1000 }] },
      CD:   { handling_cents: 800,  per_item_cents: 250, tiers: [{ up_to: 2, per_item_cents: 250 }, { up_to: null, per_item_cents: 500 }] }
    }
  };
}

function planBandBlock(startRow, bands) {
  const n = Math.max((bands || []).length, 1);
  return {
    titleRow: startRow,
    headerRow: startRow + 1,
    dataStart: startRow + 2,
    dataEnd: startRow + 1 + n,
    nextRow: startRow + 3 + n
  };
}

/**
 * Interactive flat-rate shortfall simulator — edit cart qty, weights, and flat rates
 * to see when flat-rate falls below weight-based postage (US/INTL/CA).
 */
function buildFlatRateSimulatorSheet(shippingSettings) {
  const frc = loadFlatRateConfig(shippingSettings);
  const dest = frc?.destinations || defaultFlatDestinations();
  const fuelDefault = frc?.fuel_surcharge_pct || 0;

  const { usBands, intlBands, caParcel, caFuelPct } = buildWeightShippingFns(shippingSettings);

  const data = [];
  const er = () => data.length; // next excel row number after push

  // ── Header & cart inputs ──
  data.push(['FLAT-RATE SHORTFALL SIMULATOR']);
  data.push(['Edit column B (cart) and columns C–F (flat rates). SHORT = flat rate below weight-based postage.']);
  data.push([]);
  data.push(['── CART INPUTS (edit) ──']);
  data.push(['# Books', 3]);                                    // row 5
  data.push(['# CDs', 1]);                                      // row 6
  data.push(['Book weight (kg each)', DEFAULT_BOOK_KG]);         // row 7
  data.push(['CD weight (kg each)', DEFAULT_CD_KG]);            // row 8
  data.push(['Estimated total weight (kg)', { f: 'B5*B7+B6*B8' }]); // row 9
  data.push(['Flat-rate fuel surcharge (%)', fuelDefault]);       // row 10
  data.push([]);

  // ── Flat-rate rates ──
  data.push(['── FLAT-RATE RATES (edit $ amounts — pre-filled from admin) ──']);
  data.push(['Destination', 'Book Base ($)', 'Book Postage Adj. / Item ($)', 'CD Base ($)', 'CD Postage Adj. / Item ($)']);

  const rateRows = {};
  [['Canada (CA)', 'CA'], ['USA (US)', 'US'], ['International (INTL)', 'INTL']].forEach(([label, key]) => {
    rateRows[key] = er() + 1;
    const r = dest[key]?.Book || {};
    const c = dest[key]?.CD || {};
    data.push([
      label,
      (r.handling_cents || 0) / 100,
      (r.per_item_cents || 0) / 100,
      (c.handling_cents || 0) / 100,
      (c.per_item_cents || 0) / 100
    ]);
  });
  data.push([]);

  const SENS_ROWS = 20;

  // Layout: compute row numbers before pushing formula sections
  const baseLen = data.length;
  const covDataStart = baseLen + 3;   // section title + column header + first data row
  const sensDataStart = baseLen + 11; // after coverage block (8 rows) + sensitivity title/header
  const sensDataEnd = sensDataStart + SENS_ROWS - 1;
  const BAND_START = sensDataEnd + 6; // blank + tip + blank + section title + blank, then USA bands

  const usPlan = planBandBlock(BAND_START, usBands);
  const intlPlan = planBandBlock(usPlan.nextRow + 1, intlBands);
  const caPlan = planBandBlock(intlPlan.nextRow + 1, caParcel);

  const usLookup = bandLookupFormula('$B$9', usPlan.dataStart, usPlan.dataEnd);
  const intlLookup = bandLookupFormula('$B$9', intlPlan.dataStart, intlPlan.dataEnd);
  const caLookup = bandLookupFormula('$B$9', caPlan.dataStart, caPlan.dataEnd);

  // ── Coverage check ──
  data.push(['── COVERAGE CHECK (live — current cart) ──']);
  data.push([
    'Destination', 'Est. Weight (kg)', 'Weight-Band Ship ($)', 'Flat-Rate Ship ($)',
    'Difference ($)', 'Covers Postage?'
  ]);

  [
    ['Canada (CA)', caLookup, rateRows.CA],
    ['USA (US)', usLookup, rateRows.US],
    ['International (INTL)', intlLookup, rateRows.INTL]
  ].forEach(([label, wtFormula, rateRow], i) => {
    const row = covDataStart + i;
    data.push([
      label,
      { f: '$B$9' },
      { f: wtFormula },
      { f: flatRateFormula(rateRow, '$B$5', '$B$6', '$B$10') },
      { f: `D${row}-C${row}` },
      { f: shortfallFormula(`D${row}`, `C${row}`) }
    ]);
  });
  data.push([]);
  data.push(['Difference = Flat − Weight-Band. Negative = SHORT (you lose money on postage).']);
  data.push([]);

  // ── Sensitivity grid ──
  data.push(['── SENSITIVITY: # BOOKS vs SHORTFALL (CDs fixed from B6, weights from B7/B8) ──']);
  data.push([
    '# Books', 'Est. Weight (kg)', 'US Wt-Band ($)', 'US Flat ($)', 'US Diff', 'US Status',
    'INTL Wt-Band ($)', 'INTL Flat ($)', 'INTL Diff', 'INTL Status'
  ]);

  for (let n = 1; n <= SENS_ROWS; n++) {
    const row = sensDataStart + n - 1;
    const wtF = `A${row}*$B$7+$B$6*$B$8`;
    const usWtF = bandLookupFormula(wtF, usPlan.dataStart, usPlan.dataEnd);
    const intlWtF = bandLookupFormula(wtF, intlPlan.dataStart, intlPlan.dataEnd);
    const usFlatF = flatRateFormula(rateRows.US, `A${row}`, '$B$6', '$B$10');
    const intlFlatF = flatRateFormula(rateRows.INTL, `A${row}`, '$B$6', '$B$10');

    data.push([
      n,
      { f: wtF },
      { f: usWtF },
      { f: usFlatF },
      { f: `D${row}-C${row}` },
      { f: shortfallFormula(`D${row}`, `C${row}`) },
      { f: intlWtF },
      { f: intlFlatF },
      { f: `H${row}-G${row}` },
      { f: shortfallFormula(`H${row}`, `G${row}`) }
    ]);
  }

  data.push([]);
  data.push(['Tip: Raise Book/CD base or postage adjustment rates until SHORT rows turn OK.']);
  data.push([]);

  // ── Weight band reference tables ──
  const pushBandBlockAt = (plan, title, bands, fuelPct = 0) => {
    while (data.length < plan.titleRow - 1) data.push([]);
    data.push([title]);
    data.push(['Max kg', 'Price ($)']);
    const sorted = [...(bands || [])].sort((a, b) => a.weight_kg - b.weight_kg);
    const rows = sorted.length ? sorted : [{ weight_kg: 0, price_cents: 0 }];
    rows.forEach(b => {
      let price = b.price_cents / 100;
      if (fuelPct) price = Math.round(price * (1 + fuelPct / 100) * 100) / 100;
      data.push([b.weight_kg, price]);
    });
    while (data.length < plan.dataEnd) data.push(['', '']);
  };

  data.push(['── WEIGHT BAND TABLES (from admin — used for Weight-Band Ship column) ──']);
  data.push([]);
  while (data.length < BAND_START - 1) data.push([]);

  pushBandBlockAt(usPlan, 'USA Weight Bands', usBands);
  while (data.length < usPlan.nextRow) data.push([]);
  pushBandBlockAt(intlPlan, 'International Weight Bands', intlBands);
  while (data.length < intlPlan.nextRow) data.push([]);
  pushBandBlockAt(
    caPlan,
    `Canada Parcel Bands${caFuelPct ? ` (incl. ${caFuelPct}% fuel)` : ''}`,
    caParcel,
    caFuelPct
  );

  const sheet = autoCols(ws(data));
  sheet['!cols'] = [
    { wch: 22 }, { wch: 14 }, { wch: 16 }, { wch: 14 }, { wch: 12 }, { wch: 14 },
    { wch: 16 }, { wch: 14 }, { wch: 12 }, { wch: 14 }
  ];
  return sheet;
}

/* ── 7) Multi-Book Scenarios sheet ──────────────────────────── */

function lookupBandPrice(weightKg, bands) {
  if (!bands?.length || weightKg <= 0) return null;
  const sorted = [...bands].sort((a, b) => a.weight_kg - b.weight_kg);
  const hit = sorted.find((b) => b.weight_kg >= weightKg);
  return hit ? hit.price_cents : sorted[sorted.length - 1].price_cents;
}

function buildMultiBookSheet(shippingSettings, taxSettings) {
  const {
    caParcel, caLettermail, caMaxLetter, caFlatBoxes, caShipping, usShipping, intlShipping, caFreeThreshold, us, intl
  } = buildWeightShippingFns(shippingSettings);

  const flatCfg = loadFlatRateConfig(shippingSettings);
  const flatEnabled = flatCfg?.enabled;

  // Find key CA provinces for tax examples
  const caProvs = (taxSettings || []).filter(t => t.zone === 'CA');
  const ns = caProvs.find(p => p.province_code === 'NS');
  const on = caProvs.find(p => p.province_code === 'ON');

  const bookWeights = [0.30, 0.35, 0.40];
  const bookPrice = DEFAULT_BOOK_PRICE;

  const data = [];
  const df = '$#,##0.00';

  data.push(['MULTI-BOOK SHIPPING SCENARIOS']);
  data.push(['Pre-calculated shipping using current admin weight bands. Estimated weight = qty × book weight.']);
  data.push([`Book price: $${bookPrice} CAD each. Free shipping threshold (CA): $${caFreeThreshold}`]);
  if (flatEnabled) {
    data.push(['Flat-rate columns included for comparison — negative shortfall means flat rate may not cover postage.']);
  }
  data.push([]);

  bookWeights.forEach(bw => {
    data.push([`── ${(bw * 1000).toFixed(0)}g Book ($${bookPrice} each) ──`]);
    const headers = [
      '# Books', 'Est. Weight (kg)', 'Subtotal ($)',
      'Canada Ship ($)', 'CA Method',
      'US Weight Ship ($)', 'INTL Weight Ship ($)'
    ];
    if (flatEnabled) {
      headers.push('US Flat ($)', 'US Shortfall', 'INTL Flat ($)', 'INTL Shortfall');
    }
    headers.push(
      `NS Tax (${pct(ns?.general_rate)})`, `ON Tax (${pct(on?.general_rate)})`,
      'NS Order Total', 'US Order Total', 'INTL Order Total'
    );
    data.push(headers);

    for (let n = 1; n <= 10; n++) {
      const wt = +(bw * n).toFixed(3);
      const subtotal = +(bookPrice * n).toFixed(2);
      const subtotalCents = Math.round(subtotal * 100);

      const isCaFree = subtotal >= caFreeThreshold;
      const caShipCents = isCaFree ? 0 : caShipping(wt, subtotal);
      const usShipCents = usShipping(wt);
      const intlShipCents = intlShipping(wt);

      let caMethod = '';
      if (isCaFree) {
        caMethod = 'FREE';
      } else if (wt <= caMaxLetter && caLettermail.length) {
        caMethod = 'Lettermail';
      } else {
        const parcelP = caParcel.length ? lookupBandPrice(wt, caParcel) : null;
        const flatP = (wt <= 5 && caFlatBoxes.length) ? Math.min(...caFlatBoxes.map(b => b.price_cents)) : null;
        if (parcelP != null && flatP != null) {
          caMethod = parcelP <= flatP ? 'Parcel Band' : 'Flat-Rate Box';
        } else if (parcelP != null) {
          caMethod = 'Parcel Band';
        } else if (flatP != null) {
          caMethod = 'Flat-Rate Box';
        } else {
          caMethod = 'Standard Rate';
        }
      }

      const nsTaxRate = ns?.books_rate || 0.05;
      const onTaxRate = on?.books_rate || 0.05;
      const nsShipTaxRate = ns?.shipping_tax_rate || 0.14;
      const onShipTaxRate = on?.shipping_tax_rate || 0.13;

      const nsTax = Math.round(subtotalCents * nsTaxRate + caShipCents * nsShipTaxRate) / 100;
      const onTax = Math.round(subtotalCents * onTaxRate + caShipCents * onShipTaxRate) / 100;

      const nsTotal = subtotal + caShipCents / 100 + nsTax;
      const usTotal = subtotal + usShipCents / 100;
      const intlTotal = subtotal + intlShipCents / 100;

      const row = [
        n, wt, subtotal,
        caShipCents / 100, caMethod,
        usShipCents / 100, intlShipCents / 100
      ];

      if (flatEnabled) {
        const usFlat = flatRateBooksOnlyCents(flatCfg, 'US', n);
        const intlFlat = flatRateBooksOnlyCents(flatCfg, 'INTL', n);
        row.push(
          usFlat != null ? usFlat / 100 : 'N/A',
          usFlat != null ? coverageLabel(usFlat, usShipCents) : 'N/A',
          intlFlat != null ? intlFlat / 100 : 'N/A',
          intlFlat != null ? coverageLabel(intlFlat, intlShipCents) : 'N/A'
        );
      }

      row.push(nsTax, onTax, +nsTotal.toFixed(2), +usTotal.toFixed(2), +intlTotal.toFixed(2));
      data.push(row);
    }
    data.push([]);
  });

  // Summary / notes
  data.push(['── NOTES ──']);
  data.push(['- Canada shipping picks cheapest of: Lettermail (≤0.5kg), Parcel Band, Flat-Rate Box, or Standard Rate.']);
  data.push(['- US and International use weight-band lookup from admin settings.']);
  data.push(['- Flat-rate columns compare per-item flat shipping vs weight bands — watch for SHORT on US/INTL.']);
  data.push(['- Canadian tax on books = GST only (5%); provincial HST portion is rebated (point-of-sale rebate).']);
  data.push(['- Tax on shipping varies by province (NS=14%, ON=13%, AB=5%).']);
  data.push(['- US/INTL orders have $0 tax (zero-rated exports). US tariff (if enabled) is NOT included in these scenarios.']);
  data.push([`- Free shipping applies when subtotal ≥ $${caFreeThreshold} (CA). US free threshold: $${Number(us?.free_threshold) || 500}. INTL: $${Number(intl?.free_threshold) || 500}.`]);

  const sheet = XLSX.utils.aoa_to_sheet(data);

  // Format dollar columns
  const range = XLSX.utils.decode_range(sheet['!ref'] || 'A1');
  for (let R = 0; R <= range.e.r; ++R) {
    // Columns C(2), D(3), F(5), G(6), H(7), I(8), J(9), K(10), L(11) could have dollar values
    [2, 3, 5, 6, 7, 8, 9, 10, 11].forEach(C => {
      const addr = XLSX.utils.encode_cell({ r: R, c: C });
      const cell = sheet[addr];
      if (cell && typeof cell.v === 'number' && cell.v > 0) {
        cell.z = df;
      }
    });
  }

  sheet['!cols'] = [
    { wch: 10 }, { wch: 14 }, { wch: 12 },
    { wch: 16 }, { wch: 16 },
    { wch: 16 }, { wch: 16 },
    { wch: 14 }, { wch: 14 },
    { wch: 16 }, { wch: 16 }, { wch: 16 }
  ];

  return sheet;
}

/* ── 8) Hardcoded Fallback Rates sheet ─────────────────────── */

/* ── 8) Flat-Rate Shipping sheet ────────────────────────────── */

function calcFlatRateOrder(activeCfg, dest, bookQty, cdQty) {
  const dRates = activeCfg?.destinations?.[dest];
  if (!dRates) return null;

  const bookH = dRates.Book?.handling_cents || 0;
  const cdH = dRates.CD?.handling_cents || 0;
  const maxH = Math.max(bookH, cdH);
  const bookPer = tieredPerItemCents(bookQty, dRates.Book);
  const cdPer = tieredPerItemCents(cdQty, dRates.CD);
  const subCents = maxH + bookPer + cdPer;
  const fuelPct = activeCfg?.fuel_surcharge_pct || 0;
  const fuelCents = fuelPct > 0 ? Math.round(subCents * fuelPct / 100) : 0;
  const shippingCents = subCents + fuelCents;

  return {
    bookH, cdH, maxH, bookPer, cdPer, subCents, fuelCents, shippingCents,
    bookHCad: bookH / 100,
    cdHCad: cdH / 100,
    maxHCad: maxH / 100,
    bookPerCad: bookPer / 100,
    cdPerCad: cdPer / 100,
    subCad: subCents / 100,
    fuelCad: fuelCents / 100,
    shippingCad: shippingCents / 100
  };
}

function calcCanadaTaxCents(booksCents, cdsCents, shippingCents, prov) {
  const booksTax = Math.round(booksCents * (prov.books_rate || 0));
  const cdsTax = Math.round(cdsCents * (prov.general_rate || 0));
  const shipTax = Math.round(shippingCents * (prov.shipping_tax_rate || 0));
  return { booksTax, cdsTax, shipTax, total: booksTax + cdsTax + shipTax };
}

function buildFlatRateSheet(shippingSettings, taxSettings) {
  const rows = [
    ['Flat-Rate Shipping Configuration'],
    ['Base shipping & handling = highest applicable type per order. Postage adjustment × quantity per type. Total = base + postage adjustment + fuel surcharge.'],
    []
  ];

  // Collect flat-rate configs from all countries that have one
  const configs = [];
  (shippingSettings || []).forEach(s => {
    let frc = s.flat_rate_config;
    if (!frc) return;
    if (typeof frc === 'string') { try { frc = JSON.parse(frc); } catch { return; } }
    configs.push({ country: s.country_code, name: s.country_name, config: frc });
  });

  if (configs.length === 0) {
    rows.push(['No flat-rate configurations found. Enable flat-rate in Admin → Shipping for any country.']);
    return autoCols(ws(rows));
  }

  const activeCfg = configs.find(c => c.config.enabled)?.config || configs[0]?.config;
  const caProvs = (taxSettings || []).filter(t => t.zone === 'CA');
  const { caShipping, usShipping, intlShipping } = buildWeightShippingFns(shippingSettings);

  // Scenario defaults (1 CD + 3 Books)
  const BOOK_PRICE = DEFAULT_BOOK_PRICE;
  const CD_PRICE = DEFAULT_CD_PRICE;
  const BOOK_QTY = 3;
  const CD_QTY = 1;
  const BOOK_WT = DEFAULT_BOOK_KG;
  const CD_WT = DEFAULT_CD_KG;
  const estWeightKg = estimateCartWeightKg(BOOK_QTY, CD_QTY, BOOK_WT, CD_WT);
  const booksCents = Math.round(BOOK_PRICE * BOOK_QTY * 100);
  const cdsCents = Math.round(CD_PRICE * CD_QTY * 100);
  const merchCents = booksCents + cdsCents;
  const merchCad = merchCents / 100;

  // Rates table
  rows.push(['Source Country', 'Status', 'Fuel Surcharge %', 'Destination', 'Item Type', 'Base ($)', 'Postage Adj. / Item ($)']);

  configs.forEach(({ country, name, config }) => {
    const dests = config.destinations || {};
    const status = config.enabled ? 'ACTIVE' : 'OFF';
    const fuelPct = config.fuel_surcharge_pct || 0;
    let first = true;
    ['CA', 'US', 'INTL'].forEach(dest => {
      const destRates = dests[dest];
      if (!destRates) return;
      ['Book', 'CD'].forEach(type => {
        const r = destRates[type];
        if (!r) return;
        rows.push([
          first ? `${country} (${name})` : '',
          first ? status : '',
          first ? fuelPct : '',
          dest === 'CA' ? '🇨🇦 Canada' : dest === 'US' ? '🇺🇸 USA' : '🌍 International',
          type,
          (r.handling_cents || 0) / 100,
          (r.per_item_cents || 0) / 100
        ]);
        first = false;
      });
    });
  });

  rows.push([]);
  rows.push(['── Scenario Calculator ──']);
  rows.push(['Example cart: 3 Books @ $' + BOOK_PRICE.toFixed(2) + ' + 1 CD @ $' + CD_PRICE.toFixed(2)]);
  rows.push(['Merchandise Subtotal:', dollars(merchCad)]);
  rows.push(['Estimated Weight (books):', `${BOOK_QTY} × ${BOOK_WT} kg = ${(BOOK_QTY * BOOK_WT).toFixed(2)} kg`]);
  rows.push(['Estimated Weight (CDs):', `${CD_QTY} × ${CD_WT} kg = ${(CD_QTY * CD_WT).toFixed(2)} kg`]);
  rows.push(['Total Estimated Weight:', `${estWeightKg} kg`]);
  rows.push([]);

  const destinations = ['CA', 'US', 'INTL'];
  const destLabels = { CA: 'Canada', US: 'USA', INTL: 'International' };

  // Flat-rate vs weight-based comparison (key check for US/INTL coverage)
  rows.push(['── Flat-Rate vs Weight-Based Shipping (coverage check) ──']);
  rows.push([
    'Destination', 'Est. Weight (kg)', 'Weight-Based Ship ($)', 'Flat-Rate Ship ($)',
    'Difference ($)', 'Covers Postage?'
  ]);

  const shippingByDest = {};
  destinations.forEach(dest => {
    const flatCalc = calcFlatRateOrder(activeCfg, dest, BOOK_QTY, CD_QTY);
    if (!flatCalc) {
      rows.push([destLabels[dest], estWeightKg, 'N/A', 'N/A', 'N/A', 'N/A']);
      return;
    }
    shippingByDest[dest] = flatCalc;

    const weightCents = dest === 'CA'
      ? caShipping(estWeightKg, merchCad)
      : dest === 'US'
        ? usShipping(estWeightKg)
        : intlShipping(estWeightKg);
    const diffCents = flatCalc.shippingCents - weightCents;
    const diffCad = diffCents / 100;
    rows.push([
      destLabels[dest],
      estWeightKg,
      dollars(weightCents / 100),
      dollars(flatCalc.shippingCad),
      (diffCents >= 0 ? '+' : '-') + '$' + Math.abs(diffCad).toFixed(2),
      coverageLabel(flatCalc.shippingCents, weightCents)
    ]);
  });

  rows.push([]);
  rows.push(['Note: Negative difference / SHORT = flat rate may not cover actual postage — consider raising the postage adjustment or base rates.']);
  rows.push([]);

  rows.push(['Scenario: 1 CD + 3 Books — flat-rate breakdown']);
  rows.push(['Destination', 'Est. Weight (kg)', 'Book Base', 'CD Base', 'Base (max)', '3× Book', '1× CD', 'Subtotal', 'Fuel', 'Flat-Rate Total']);

  destinations.forEach(dest => {
    const calc = shippingByDest[dest];
    if (!calc) {
      rows.push([destLabels[dest], estWeightKg, 'N/A']);
      return;
    }
    rows.push([
      destLabels[dest],
      estWeightKg,
      dollars(calc.bookHCad),
      dollars(calc.cdHCad),
      dollars(calc.maxHCad),
      dollars(calc.bookPerCad),
      dollars(calc.cdPerCad),
      dollars(calc.subCad),
      calc.fuelCents > 0 ? dollars(calc.fuelCad) : '$0.00',
      dollars(calc.shippingCad)
    ]);
  });

  rows.push([]);
  rows.push(['── Order Totals with Tax ──']);
  rows.push([
    'Destination', 'Merchandise ($)', 'Shipping ($)', 'Tax on Books ($)', 'Tax on CD ($)',
    'Tax on Shipping ($)', 'Total Tax ($)', 'Order Total ($)'
  ]);

  // USA & International — no Canadian tax
  ['US', 'INTL'].forEach(dest => {
    const calc = shippingByDest[dest];
    if (!calc) return;
    rows.push([
      destLabels[dest],
      dollars(merchCad),
      dollars(calc.shippingCad),
      '$0.00', '$0.00', '$0.00', '$0.00',
      dollars(merchCad + calc.shippingCad)
    ]);
  });

  // Canada — show NS & ON as quick reference, full province table below
  const caShip = shippingByDest.CA;
  if (caShip && caProvs.length > 0) {
    const ns = caProvs.find(p => p.province_code === 'NS');
    const on = caProvs.find(p => p.province_code === 'ON');
    [ns, on].filter(Boolean).forEach(prov => {
      const tax = calcCanadaTaxCents(booksCents, cdsCents, caShip.shippingCents, prov);
      rows.push([
        `Canada — ${prov.province_name}`,
        dollars(merchCad),
        dollars(caShip.shippingCad),
        dollars(tax.booksTax / 100),
        dollars(tax.cdsTax / 100),
        dollars(tax.shipTax / 100),
        dollars(tax.total / 100),
        dollars(merchCad + caShip.shippingCad + tax.total / 100)
      ]);
    });
  } else if (caShip) {
    rows.push([
      'Canada',
      dollars(merchCad),
      dollars(caShip.shippingCad),
      'N/A', 'N/A', 'N/A', 'N/A',
      dollars(merchCad + caShip.shippingCad)
    ]);
  }

  // Full Canada province tax table (flat-rate shipping to CA)
  if (caShip && caProvs.length > 0) {
    rows.push([]);
    rows.push(['── Canada Tax by Province (flat-rate to Canada, 1 CD + 3 Books) ──']);
    rows.push([
      'Province', 'Code', 'Books Rate', 'General Rate', 'Ship Tax Rate',
      'Tax on Books ($)', 'Tax on CD ($)', 'Tax on Shipping ($)', 'Total Tax ($)', 'Order Total ($)'
    ]);

    caProvs.forEach(prov => {
      const tax = calcCanadaTaxCents(booksCents, cdsCents, caShip.shippingCents, prov);
      rows.push([
        prov.province_name || '',
        prov.province_code || '',
        pct(prov.books_rate),
        pct(prov.general_rate),
        pct(prov.shipping_tax_rate),
        dollars(tax.booksTax / 100),
        dollars(tax.cdsTax / 100),
        dollars(tax.shipTax / 100),
        dollars(tax.total / 100),
        dollars(merchCad + caShip.shippingCad + tax.total / 100)
      ]);
    });

    rows.push([]);
    rows.push(['Tax notes:']);
    rows.push(['• Books taxed at books_rate (usually 5% GST point-of-sale rebate)']);
    rows.push(['• CDs taxed at general_rate (full provincial/HST rate)']);
    rows.push(['• Shipping taxed at shipping_tax_rate (may differ from merchandise rate)']);
    rows.push(['• USA & International: no tax in current configuration']);
  }

  rows.push([]);
  rows.push(['Note: Flat-rate shipping uses the highest base shipping & handling amount among item types in the order,']);
  rows.push(['plus a per-item postage adjustment for each item. If fuel surcharge is enabled, it is applied on top of the subtotal.']);
  rows.push(['Estimated shipping — if overcharged, it\'s refunded.']);

  return autoCols(ws(rows));
}

function buildFallbackRatesSheet() {
  const rows = [
    ['Hardcoded Fallback Rates (used when API is unavailable)'],
    [],
    ['── Country Shipping Rates ──'],
    ['Country', 'Standard ($)', 'Expedited ($)', 'Express ($)', 'Free Threshold ($)']
  ];

  const rates = {
    US: { standard: 5.99, expedited: 12.99, express: 19.99, freeThreshold: 75 },
    CA: { standard: 8.99, expedited: 15.99, express: 24.99, freeThreshold: 50 },
    UK: { standard: 12.99, expedited: 19.99, express: 29.99, freeThreshold: 125 },
    AU: { standard: 15.99, expedited: 22.99, express: 34.99, freeThreshold: 150 },
    DE: { standard: 11.99, expedited: 18.99, express: 27.99, freeThreshold: 120 },
    FR: { standard: 11.99, expedited: 18.99, express: 27.99, freeThreshold: 120 },
    JP: { standard: 14.99, expedited: 21.99, express: 32.99, freeThreshold: 140 },
  };

  Object.entries(rates).forEach(([code, r]) => {
    rows.push([code, r.standard, r.expedited, r.express, r.freeThreshold]);
  });

  rows.push([]);
  rows.push(['── Shipping Method Definitions ──']);
  rows.push(['Method', 'Base Rate ($)', 'Free Threshold ($)', 'Max Weight (lbs)', 'Weight Rate ($/lb)', 'Delivery']);
  rows.push(['Standard', 5.99, 75, 50, 0.50, '5-7 business days']);
  rows.push(['Expedited', 12.99, 100, 50, 0.75, '2-3 business days']);
  rows.push(['Express', 19.99, 150, 30, 1.00, '1-2 business days']);
  rows.push(['International', 24.99, 200, 50, 1.50, '7-14 business days']);

  rows.push([]);
  rows.push(['── Hardcoded Tax Rates (Canada) ──']);
  rows.push(['Province', 'Code', 'Combined Rate', 'Tax Type']);

  const caTax = {
    AB: [0.05, 'GST only'], BC: [0.12, 'GST + PST'], MB: [0.12, 'GST + RST'],
    NB: [0.15, 'HST'], NL: [0.15, 'HST'], NS: [0.14, 'HST (as of Apr 2025)'],
    ON: [0.13, 'HST'], PE: [0.15, 'HST'], QC: [0.14975, 'GST + QST'],
    SK: [0.11, 'GST + PST'], NT: [0.05, 'GST only'], NU: [0.05, 'GST only'],
    YT: [0.05, 'GST only']
  };
  const provNames = {
    AB: 'Alberta', BC: 'British Columbia', MB: 'Manitoba', NB: 'New Brunswick',
    NL: 'Newfoundland & Labrador', NS: 'Nova Scotia', ON: 'Ontario',
    PE: 'Prince Edward Island', QC: 'Quebec', SK: 'Saskatchewan',
    NT: 'Northwest Territories', NU: 'Nunavut', YT: 'Yukon'
  };

  Object.entries(caTax).forEach(([code, [rate, type]]) => {
    rows.push([provNames[code] || code, code, pct(rate), type]);
  });

  return autoCols(ws(rows));
}

/* ── Quick Simulator Export ────────────────────────────────── */
/* Workbook sheets:
   1) Active Settings — current rates, tax, tariff, fuel at a glance
   2) Flat-Rate Simulator — edit cart/rates, see SHORTFALL vs weight bands
   3) Simulator — editable product inputs with live formulas
   4) Flat-Rate Scenarios — static example with tax (when flat-rate configured)
*/

export async function exportQuickSimulator() {
  const [shippingRes, taxRes] = await Promise.all([
    fetch('/api/shipping-settings'),
    fetch('/api/tax-settings')
  ]);
  if (!shippingRes.ok) throw new Error(`Failed to fetch shipping settings: ${shippingRes.status}`);
  if (!taxRes.ok) throw new Error(`Failed to fetch tax settings: ${taxRes.status}`);

  const shippingSettings = await shippingRes.json();
  const taxSettings = await taxRes.json();

  shippingSettings.forEach(s => {
    if (typeof s.weight_thresholds === 'string') { try { s.weight_thresholds = JSON.parse(s.weight_thresholds); } catch { s.weight_thresholds = null; } }
    if (typeof s.flat_rate_config === 'string') { try { s.flat_rate_config = JSON.parse(s.flat_rate_config); } catch { s.flat_rate_config = null; } }
  });

  const ca   = shippingSettings.find(s => s.country_code === 'CA');
  const us   = shippingSettings.find(s => s.country_code === 'US');
  const intl = shippingSettings.find(s => s.country_code === 'INTL');
  const caProvinces = (taxSettings || []).filter(t => t.zone === 'CA');

  // Parse CA weight thresholds
  const caWt = ca?.weight_thresholds || {};
  const letterBands = caWt.lettermail_bands || (Array.isArray(caWt) ? caWt : []);
  const parcelBands = caWt.parcel_bands || [];
  const fuelPct     = caWt.fuel_surcharge_pct || 0;
  const flatBoxes   = caWt.flat_rate_boxes || [];

  // Flat-rate config (use from CA, but destinations cover all)
  let frc = ca?.flat_rate_config || us?.flat_rate_config || intl?.flat_rate_config || null;
  const flatEnabled = frc?.enabled || false;

  // ── Sheet 1: Active Settings ──
  const s1 = [];
  s1.push(['CURRENT ACTIVE SHIPPING, TAX, TARIFF & FUEL SETTINGS']);
  s1.push([`Generated: ${new Date().toLocaleString()}`]);
  s1.push([]);

  // Shipping mode
  s1.push(['══ SHIPPING MODE ══']);
  s1.push(['Active Mode:', flatEnabled ? 'FLAT-RATE' : 'WEIGHT-BASED (Canada Post)']);
  s1.push([]);
  s1.push(['══ ESTIMATED WEIGHT DEFAULTS (export scenarios) ══']);
  s1.push(['Book (per unit):', `${DEFAULT_BOOK_KG} kg`]);
  s1.push(['CD (per unit):', `${DEFAULT_CD_KG} kg`]);
  s1.push(['Example cart (3 books + 1 CD):', `${estimateCartWeightKg(3, 1)} kg`]);
  s1.push([]);
  s1.push(['══ ESTIMATED PRICE DEFAULTS (export scenarios) ══']);
  s1.push(['Book (per unit):', `$${DEFAULT_BOOK_PRICE.toFixed(2)}`]);
  s1.push(['CD (per unit):', `$${DEFAULT_CD_PRICE.toFixed(2)}`]);
  s1.push(['Example cart (3 books + 1 CD):', `$${(3 * DEFAULT_BOOK_PRICE + DEFAULT_CD_PRICE).toFixed(2)}`]);
  s1.push(['See "Flat-Rate Simulator" sheet — edit rates/qty to find SHORTFALL rows.']);
  s1.push([]);

  // ── Standard rates ──
  s1.push(['══ STANDARD SHIPPING RATES ══']);
  s1.push(['Country', 'Standard ($)', 'Expedited ($)', 'Express ($)', 'Free Threshold ($)', 'Enabled']);
  [['Canada',        ca],
   ['USA',           us],
   ['International', intl]].forEach(([label, s]) => {
    if (!s) return;
    s1.push([label, s.standard_rate, s.expedited_rate, s.express_rate, s.free_threshold, s.enabled ? 'Yes' : 'No']);
  });
  s1.push([]);

  // ── Weight bands (CA) ──
  if (!flatEnabled && (letterBands.length || parcelBands.length)) {
    s1.push(['══ CANADA WEIGHT BANDS (active) ══']);
    if (letterBands.length) {
      s1.push(['Lettermail Bands:']);
      s1.push(['Max Weight (kg)', 'Price ($)']);
      letterBands.sort((a, b) => a.weight_kg - b.weight_kg).forEach(b => {
        s1.push([b.weight_kg, (b.price_cents / 100).toFixed(2)]);
      });
      s1.push([]);
    }
    if (parcelBands.length) {
      s1.push(['Parcel Bands (base rate before fuel):']);
      s1.push(['Max Weight (kg)', 'Base ($)', fuelPct > 0 ? `+ Fuel ${fuelPct}% ($)` : '', fuelPct > 0 ? 'Total ($)' : '']);
      [...parcelBands].sort((a, b) => a.weight_kg - b.weight_kg).forEach(b => {
        const base = b.price_cents / 100;
        const fuel = fuelPct > 0 ? Math.round(b.price_cents * fuelPct / 100) / 100 : 0;
        s1.push([b.weight_kg, base.toFixed(2), fuelPct > 0 ? fuel.toFixed(2) : '', fuelPct > 0 ? (base + fuel).toFixed(2) : '']);
      });
      s1.push([]);
    }
    if (flatBoxes.length) {
      s1.push(['Flat-Rate Boxes:']);
      s1.push(['Max Length (cm)', 'Price ($)']);
      flatBoxes.forEach(b => s1.push([b.max_length_cm, (b.price_cents / 100).toFixed(2)]));
      s1.push([]);
    }
  }

  // ── Flat-rate config ──
  if (flatEnabled && frc) {
    s1.push(['══ FLAT-RATE SHIPPING RATES (active) ══']);
    s1.push(['Fuel Surcharge:', frc.fuel_surcharge_pct > 0 ? `${frc.fuel_surcharge_pct}%` : 'OFF']);
    s1.push([]);
    s1.push(['Destination', 'Item Type', 'Base ($)', 'Postage Adj. / Item ($)']);
    const destLabels = { CA: 'Canada', US: 'USA', INTL: 'International' };
    ['CA', 'US', 'INTL'].forEach(dest => {
      const d = frc.destinations?.[dest];
      if (!d) return;
      ['Book', 'CD'].forEach(type => {
        const r = d[type];
        if (!r) return;
        s1.push([destLabels[dest], type, (r.handling_cents / 100).toFixed(2), (r.per_item_cents / 100).toFixed(2)]);
      });
    });
    s1.push([]);
  }

  // ── Fuel surcharge ──
  s1.push(['══ FUEL SURCHARGE ══']);
  s1.push(['Canada Post Fuel %:', fuelPct > 0 ? `${fuelPct}%` : 'OFF']);
  if (flatEnabled && frc) {
    s1.push(['Flat-Rate Fuel %:', (frc.fuel_surcharge_pct || 0) > 0 ? `${frc.fuel_surcharge_pct}%` : 'OFF']);
  }
  s1.push([]);

  // ── Tariff ──
  s1.push(['══ U.S. TARIFF ══']);
  s1.push(['Tariff Enabled:', us?.tariff_enabled ? 'Yes' : 'No']);
  s1.push(['Tariff Rate:', us?.tariff_rate ? `${(us.tariff_rate * 100).toFixed(1)}%` : 'N/A']);
  s1.push(['Exemption Threshold:', us?.tariff_exemption_threshold_usd ? `$${us.tariff_exemption_threshold_usd} USD` : 'N/A']);
  s1.push(['Notes:', us?.tariff_notes || '']);
  s1.push([]);

  // ── Tax rates ──
  s1.push(['══ CANADIAN TAX RATES ══']);
  s1.push(['Province', 'Code', 'General Rate', 'Books Rate', 'Shipping Tax Rate']);
  caProvinces.forEach(p => {
    s1.push([
      p.province_name, p.province_code,
      `${((p.general_rate || 0) * 100).toFixed(2)}%`,
      `${((p.books_rate || 0) * 100).toFixed(2)}%`,
      `${((p.shipping_tax_rate || 0) * 100).toFixed(2)}%`
    ]);
  });

  const sheet1 = autoCols(ws(s1));
  const sheetFlat = buildFlatRateSimulatorSheet(shippingSettings);
  const sheet2 = buildSimulatorSheet(shippingSettings, taxSettings);

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, sheet1, 'Active Settings');
  XLSX.utils.book_append_sheet(wb, sheetFlat, 'Flat-Rate Simulator');
  XLSX.utils.book_append_sheet(wb, sheet2, 'Simulator');

  // Static scenarios with tax when flat-rate config exists
  if (frc) {
    XLSX.utils.book_append_sheet(wb, buildFlatRateSheet(shippingSettings, taxSettings), 'Flat-Rate Scenarios');
  }

  const now = new Date().toISOString().slice(0, 10);
  XLSX.writeFile(wb, `CranfordPub_QuickSimulator_${now}.xlsx`);
}

/* ── Main export function (full workbook) ─────────────────── */

export async function exportShippingTaxExcel() {
  // Fetch live data from the API
  const [shippingRes, taxRes] = await Promise.all([
    fetch('/api/shipping-settings'),
    fetch('/api/tax-settings')
  ]);

  if (!shippingRes.ok) throw new Error(`Failed to fetch shipping settings: ${shippingRes.status}`);
  if (!taxRes.ok) throw new Error(`Failed to fetch tax settings: ${taxRes.status}`);

  const shippingSettings = await shippingRes.json();
  const taxSettings = await taxRes.json();

  // Parse weight_thresholds strings
  shippingSettings.forEach(s => {
    if (typeof s.weight_thresholds === 'string') {
      try { s.weight_thresholds = JSON.parse(s.weight_thresholds); } catch { s.weight_thresholds = null; }
    }
  });

  // Also parse flat_rate_config strings
  shippingSettings.forEach(s => {
    if (typeof s.flat_rate_config === 'string') {
      try { s.flat_rate_config = JSON.parse(s.flat_rate_config); } catch { s.flat_rate_config = null; }
    }
  });

  const wb = XLSX.utils.book_new();

  XLSX.utils.book_append_sheet(wb, buildShippingRatesSheet(shippingSettings), 'Shipping Rates');
  XLSX.utils.book_append_sheet(wb, buildWeightThresholdsSheet(shippingSettings), 'Weight Thresholds');
  XLSX.utils.book_append_sheet(wb, buildCanadaPostSheet(shippingSettings), 'Canada Post Domestic');
  XLSX.utils.book_append_sheet(wb, buildTaxRatesSheet(taxSettings), 'Tax Rates');
  XLSX.utils.book_append_sheet(wb, buildTariffSheet(shippingSettings), 'US Tariff');
  XLSX.utils.book_append_sheet(wb, buildSimulatorSheet(shippingSettings, taxSettings), 'Simulator');
  XLSX.utils.book_append_sheet(wb, buildFlatRateSimulatorSheet(shippingSettings), 'Flat-Rate Simulator');
  XLSX.utils.book_append_sheet(wb, buildMultiBookSheet(shippingSettings, taxSettings), 'Multi-Book Scenarios');
  XLSX.utils.book_append_sheet(wb, buildFlatRateSheet(shippingSettings, taxSettings), 'Flat-Rate Shipping');
  XLSX.utils.book_append_sheet(wb, buildFallbackRatesSheet(), 'Fallback Rates');

  const now = new Date().toISOString().slice(0, 10);
  XLSX.writeFile(wb, `CranfordPub_Shipping_Tax_${now}.xlsx`);
}
